import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../auth/auth.api';
import { TSuccessResponse } from '@/types/general/TSuccessResponse';
import TPaginationParams from '@/types/general/TPaginationParams';
import TEmployee from '@/types/employee/TEmployee';
import TKPI from '@/types/kpi-management/TKPI';
import TEmployeeKPI from '@/types/kpi-management/TEmployeeKPI';

export const employeeApi = createApi({
    reducerPath: 'employeeApi',
    tagTypes: ["employee"],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getAllEmployee: builder.query<
            TSuccessResponse<TEmployee[]>, 
            { pagination?: TPaginationParams, searchKey?: string, filter?: { position_id: number, departments: number[] } }
        >({
            query: ({ pagination = {} as any, searchKey, filter }) => {
                const params: Record<any, any> = {};
                const { page = 1, limit = 100 } = pagination;

                if (page && limit) {
                    params.page = page;
                    params.limit = limit;
                }

                if (searchKey) {
                    params.search = searchKey;
                }

                if (filter?.position_id) {
                    params.position_id = filter.position_id;
                }

                if (filter?.departments && filter.departments.length > 0) {
                    params.departments = filter.departments.join(", ");
                }

                return {
                    url: "employee-management/employee",
                    params: params
                }
            }
        }),

        getEmployee: builder.query<
            TSuccessResponse<TEmployee>, 
            string
        >({
            query: (employeeId) => {
                return {
                    url: `employee-management/employee/${employeeId}`,
                }
            }
        }),

        getEmployeeKpiList: builder.query<
            TSuccessResponse<TEmployee & { kpis: Array<TEmployeeKPI> }>, 
            { employeeId: number, year?: string|number, month?: string|number }
        >({
            query: ({ employeeId , year = new Date().getFullYear(), month = (new Date().getMonth()) + 1 }) => {
                return {
                    url: `employee-management/employee/${employeeId}/kpis`,
                    params: { year, month }
                }
            }
        }),

        updateEmployeeKpi: builder.mutation<
            TSuccessResponse<null>, 
            { employeeKpiId: number, data: { kpi_result_weight: number } }
        >({
            query: ({ employeeKpiId, data }) => ({
                url: `employee-management/employee/${employeeKpiId}`,
                method: 'PATCH',
                body: data,
            }),
        }),
    }),
});

export const {
    useGetAllEmployeeQuery,
    useGetEmployeeQuery,
    useGetEmployeeKpiListQuery,
    useUpdateEmployeeKpiMutation
} = employeeApi;
